/**
 * Enum → tone.
 *
 * The *words* live in the dictionaries (src/i18n); only the colour lives here. Splitting
 * them this way is what lets a status be translated without every tone decision being
 * duplicated per language — a suspended account is amber in both languages, and nobody
 * should have to remember to keep two copies of that in step.
 *
 * Every status pairs a tone with an icon or a word at the call site: colour alone fails
 * a colourblind manager and fails the greyscale printout the shift table gets turned
 * into. See brand/BRAND.md.
 */

export type Tone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger';

export interface Label {
  label: string;
  tone: Tone;
}

/** The dictionary sections under `status.` that carry a matching tone map here. */
export type LabelGroup =
  | 'job'
  | 'shift'
  | 'user'
  | 'role'
  | 'register'
  | 'severity'
  | 'docCategory'
  | 'flag';

export const TONES: Record<LabelGroup, Record<string, Tone>> = {
  job: {
    draft: 'neutral',
    scheduled: 'primary',
    in_progress: 'success',
    on_hold: 'warning',
    completed: 'success',
    cancelled: 'danger',
  },

  shift: {
    open: 'success',
    closed: 'neutral',
    auto_closed: 'warning',
    disputed: 'danger',
  },

  user: {
    invited: 'primary',
    active: 'success',
    suspended: 'warning',
    archived: 'neutral',
  },

  role: {
    admin: 'danger',
    manager: 'primary',
    engineer: 'neutral',
  },

  register: {
    valid: 'success',
    expiring: 'warning',
    expired: 'danger',
    missing: 'danger',
    not_provided: 'neutral',
    exempt: 'neutral',
  },

  severity: {
    critical: 'danger',
    warning: 'warning',
    info: 'neutral',
  },

  docCategory: {
    contract: 'primary',
    drawing: 'primary',
    certificate: 'success',
    risk_assessment: 'warning',
    datasheet: 'neutral',
    site_photo: 'neutral',
    signed_sheet: 'success',
    signature: 'success',
    id_document: 'neutral',
    other: 'neutral',
  },

  /**
   * Exception flags. Toned neutrally on purpose: these describe an employee to their
   * manager, and anything implying dishonesty would be both wrong (GPS is noisy) and
   * corrosive. "No GPS" is neutral, not a warning — it is a fact about a basement.
   */
  flag: {
    outside_geofence: 'warning',
    mocked_location: 'warning',
    poor_accuracy: 'neutral',
    no_fix: 'neutral',
    offline_sync: 'neutral',
    auto_closed: 'warning',
    manually_adjusted: 'primary',
    no_job_assigned: 'neutral',
    overlapping_shift: 'danger',
  },
};

export function toneFor(group: LabelGroup, key: string): Tone {
  return TONES[group][key] ?? 'neutral';
}
